import React, { useEffect, useContext } from 'react';
import { useHistory } from "react-router-dom";
import ReactPixel from "react-facebook-pixel";
import { AuthContext } from "../../context/Auth/AuthContext";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useDate } from "../../hooks/useDate";
import { sendTrackingEventOnce, sendGoogleAdsConversion } from "../../utils/trackingMutex";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: theme.spacing(2),
    overflow: "auto",
  },
  paper: {
    padding: theme.spacing(4),
    textAlign: "center",
    maxWidth: 600,
    borderRadius: 16,
  },
  icon: {
    fontSize: 80,
    color: "#4caf50",
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  message: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  button: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 4),
    fontSize: "1.1rem",
    borderRadius: 8,
  },
  infoBox: {
    backgroundColor: "#e8f5e9",
    padding: theme.spacing(2.5),
    borderRadius: 12,
    marginTop: theme.spacing(2),
    textAlign: "left",
    border: "1px solid #c8e6c9",
  },
  infoItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: "1px solid #c8e6c9",
    "&:last-child": {
      marginBottom: 0,
      paddingBottom: 0,
      borderBottom: "none",
    },
  },
  label: {
    fontWeight: 600,
    color: "#2e7d32",
    fontSize: "0.95rem",
  },
  value: {
    fontWeight: 600,
    color: "#1b5e20",
    fontSize: "1rem",
  },
}));

function CadastroSucesso() {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { dateToClient } = useDate();

  // Recuperar dados do usuário do localStorage (salvo no signup)
  const getSignupUserData = () => {
    try {
      const data = localStorage.getItem('signupUserData');
      return data ? JSON.parse(data) : null;
    } catch (err) {
      console.error("Erro ao ler dados do signup:", err);
      return null;
    }
  };

  const signupUserData = getSignupUserData();

  // Usar dados do AuthContext ou fallback para localStorage
  const displayUser = user && user.email ? user : signupUserData;

  // Limpar dados temporários quando AuthContext carregar
  useEffect(() => {
    if (user && user.email && signupUserData) {
      localStorage.removeItem('signupUserData');
    }
  }, [user, signupUserData]);

  // REMOVIDO: Configuração do Google Ads (já configurado no Signup)
  // Evita configurar 2 vezes (Signup + SignupSuccess = duplicidade)
  // A flag global gtagConfigured do Signup impede reconfiguração

  useEffect(() => {
    // Recuperar valor do plano escolhido no signup
    const planValue = parseFloat(localStorage.getItem('trialPlanValue') || '0');

    // Enviar eventos de rastreamento com Mutex
    sendTrackingEventOnce('signup', async () => {
      // Facebook Pixel: ViewContent quando página é carregada
      ReactPixel.track('ViewContent', {
        content_name: 'Cadastro Sucesso - Trial Iniciado',
        content_category: 'signup_confirmation',
        content_ids: [String(displayUser?.companyId)],
        content_type: 'trial_activation',
        value: planValue,
        currency: 'BRL',
        user_email: displayUser?.email
      });

      // Google Ads: Conversion event para signup com valor do plano
      await sendGoogleAdsConversion('signup', 'sd4xCL6JipccELKMnqdD', planValue, 'BRL');

      // Limpar localStorage após usar
      localStorage.removeItem('trialPlanValue');
    });

    // REMOVIDO: Redirecionamento automático para evitar problemas com AuthContext
    // O usuário deve clicar no botão para prosseguir
  }, [displayUser]);

  const handleGoToDashboard = () => {
    // Limpar dados temporários do signup APENAS se o AuthContext já tiver os dados carregados
    if (user && user.email) {
      localStorage.removeItem('signupUserData');
      // Se o AuthContext já carregou, usa push normal
      history.push("/");
    } else {
      // Se o AuthContext ainda não carregou, força reload para garantir
      window.location.href = "/";
    }
  };

  if (!displayUser) {
    return (
      <div className={classes.container}>
        <CircularProgress style={{ color: "white" }} />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Paper className={classes.paper} elevation={3}>
        <CheckCircleIcon className={classes.icon} />

        <Typography variant="h4" className={classes.title}>
          Parabéns! 🎉
        </Typography>

        <Typography variant="h6" className={classes.message}>
          Seu cadastro foi realizado com sucesso!
        </Typography>

        <Typography variant="body1" style={{ marginBottom: 16 }}>
          Você iniciou seu <strong>período de teste gratuito de 3 dias</strong>.
          Aproveite para explorar todas as funcionalidades do ConexChat!
        </Typography>

        <Box className={classes.infoBox}>
          <div className={classes.infoItem}>
            <span className={classes.label}>Empresa:</span>
            <span className={classes.value}>{displayUser.companyName || displayUser.name || displayUser?.company?.name}</span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.label}>Email:</span>
            <span className={classes.value}>{displayUser.email}</span>
          </div>
          {displayUser.company?.dueDate && (
            <div className={classes.infoItem}>
              <span className={classes.label}>Teste válido até:</span>
              <span className={classes.value}>
                {dateToClient(displayUser.company.dueDate)}
              </span>
            </div>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          className={classes.button}
          onClick={handleGoToDashboard}
        >
          Ir para o Dashboard
        </Button>

        <Typography variant="body2" style={{ display: "block", marginTop: 16, color: "#666" }}>
          Clique no botão acima para acessar seu painel e começar a usar o sistema!
        </Typography>
      </Paper>
    </div>
  );
}

export default CadastroSucesso;